/**
 * The shared animation clock (ADR-041, P1.6-T2): the time every looping animation reads, which stops while the
 * editor's animations are paused and carries on from where it stopped when they resume. Plain data, so a
 * pause is a new value and nothing is held hidden.
 */

/**
 * A clock: `offsetMs` is the wall time spent paused so far, and `pausedAt` the wall time the current pause
 * began, or null while running.
 */
export interface AnimationClock {
  readonly offsetMs: number;
  readonly pausedAt: number | null;
}

/**
 * A clock that has never been paused.
 */
export const RUNNING_CLOCK: AnimationClock = { offsetMs: 0, pausedAt: null };

/**
 * Stops a clock.
 *
 * @param clock The clock.
 * @param nowMs Wall time in milliseconds.
 * @returns The paused clock; the same clock when it is already paused.
 */
export function pauseClock(clock: AnimationClock, nowMs: number): AnimationClock {
  return clock.pausedAt === null ? { offsetMs: clock.offsetMs, pausedAt: nowMs } : clock;
}

/**
 * Starts a paused clock again, so that its reading continues from the value it stopped at.
 *
 * @param clock The clock.
 * @param nowMs Wall time in milliseconds.
 * @returns The running clock; the same clock when it is already running.
 */
export function resumeClock(clock: AnimationClock, nowMs: number): AnimationClock {
  return clock.pausedAt === null ? clock : { offsetMs: clock.offsetMs + (nowMs - clock.pausedAt), pausedAt: null };
}

/**
 * What the clock reads.
 *
 * @param clock The clock.
 * @param nowMs Wall time in milliseconds.
 * @returns Milliseconds of running time: the time the clock was stopped at while paused.
 */
export function clockElapsed(clock: AnimationClock, nowMs: number): number {
  return (clock.pausedAt ?? nowMs) - clock.offsetMs;
}
